import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import PageLayout from '../components/layout/PageLayout'
import FloatingFabs from '@astro/floating-fabs'
import maharajPortrait from '../assets/images/maharaj-ji.png'
import { useToast } from '../context/ToastContext'
import { useRazorpayCheckout } from '../hooks/useRazorpayCheckout'
import { releaseUnpaidBooking } from '../utils/bookingCheckout'
import { toIsoDate, formatDateLabel, formatTimeLabel } from '../utils/date'
import { resolveRazorpayKey } from '../utils/payments'
import TimeSlotPicker from '../components/sections/booking/TimeSlotPicker'
import BirthDetailsForm, { isBirthDetailsValid } from '../components/sections/booking/BirthDetailsForm'
import BookingTrustSection from '../components/sections/booking/BookingTrustSection'
import { GemIcon } from '../components/sections/booking/GemstoneConsultationPromo'
import { CONSULTATION_TYPE_LABELS, useGemstoneOffer } from '../components/sections/booking/useGemstoneOffer'

// Raju Maharaj's gemstone consultation — the simpler of his two consultations.
// Every day is bookable inside the admin-set hours, the price is one fixed
// amount from the backend (GET /gemstone-consultation), and the booking goes
// through the regular POST /bookings with category "gemstone" — the server
// prices it, so no `rate` is sent from here.

const DAYS_SHOWN = 14
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function startOfToday() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

export default function MaharajGemstoneBookingPage() {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const { status: authStatus, user } = useAuth()
  const { open: openRazorpay } = useRazorpayCheckout()
  const { offer, isPending: offerPending } = useGemstoneOffer()

  const days = useMemo(() => {
    const today = startOfToday()
    return Array.from({ length: DAYS_SHOWN }, (_, i) => {
      const d = new Date(today)
      d.setDate(d.getDate() + i)
      return d
    })
  }, [])

  const [selectedDate, setSelectedDate] = useState(() => startOfToday())
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [consultationType, setConsultationType] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [birthDetails, setBirthDetails] = useState({
    birthDate: '', birthTime: '', place: '', placeLocation: null, notes: '',
    birthTimeUnknown: false, photo: null, leftPalmPhoto: null, rightPalmPhoto: null,
  })

  // Default to the first offered type; drop a choice the admin has since removed.
  useEffect(() => {
    if (!offer?.consultation_types?.length) return
    if (!consultationType || !offer.consultation_types.includes(consultationType)) {
      setConsultationType(offer.consultation_types[0])
    }
  }, [offer, consultationType])

  const { data: gatewaysData } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: () => api.payments.gateways(),
    staleTime: 10 * 60 * 1000,
  })
  const razorpayKey = resolveRazorpayKey(gatewaysData)

  const price = offer ? Number(offer.rate) : null
  const priceLabel = price != null ? `₹${price.toLocaleString('en-IN')}` : null
  const birthValid = isBirthDetailsValid(birthDetails)
  const canProceed = Boolean(offer?.is_enabled && selectedDate && selectedSlot && consultationType && birthValid) && !isSubmitting

  const handleProceed = async () => {
    if (!offer?.is_enabled || isSubmitting) return

    if (!birthValid) {
      showToast('Please fill in your date, time and place of birth to continue.')
      return
    }
    if (authStatus !== 'authenticated') {
      showToast('Please sign in to continue your booking.')
      navigate(`/login?redirect=${encodeURIComponent('/book-maharaj-ji/gemstone')}`)
      return
    }
    if (!selectedSlot) {
      showToast('Please choose a time slot to continue.')
      return
    }

    const dateLabel = formatDateLabel(selectedDate)
    const timeLabel = formatTimeLabel(selectedSlot.start_time)
    setIsSubmitting(true)

    let bookingId = null
    try {
      const bookingResponse = await api.bookings.create({
        category: 'gemstone',
        name: user?.name ?? '',
        phone: user?.mobile ?? '',
        email: user?.email ?? '',
        consultation_type: consultationType,
        date: toIsoDate(selectedDate),
        slot_id: selectedSlot.id,
        payment_method: 'razorpay',
        birth_date: birthDetails.birthDate,
        birth_time: birthDetails.birthTime,
        place: birthDetails.place,
        notes: birthDetails.notes,
      })
      const booking = bookingResponse?.data ?? bookingResponse?.booking ?? bookingResponse
      bookingId = booking?.id
      if (!bookingId) {
        showToast('Booking was reserved but its id was missing from the response — please contact support.')
        setIsSubmitting(false)
        return
      }
    } catch (err) {
      showToast(err?.error?.message || err?.message || 'Could not reserve this slot. Please try again.')
      setIsSubmitting(false)
      return
    }

    try {
      // The order amount is the booking's stored (backend) price.
      const orderResponse = await api.bookings.createRazorpayOrder({ amount: price, currency: 'INR', booking_id: bookingId })
      const order = orderResponse?.data ?? orderResponse

      const payment = await openRazorpay({
        order,
        keyId: razorpayKey,
        name: 'Raju Maharaj Ji · Gemstone Consultation',
        description: `${dateLabel} at ${timeLabel}`,
      })

      await api.bookings.verifyRazorpayPayment({
        booking_id: bookingId,
        razorpay_order_id: payment.razorpay_order_id,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_signature: payment.razorpay_signature,
      })

      showToast(`Gemstone consultation confirmed · ${priceLabel} · ${dateLabel}`)
      navigate(`/bookings/${bookingId}`)
    } catch (err) {
      // No verified payment, so nothing was booked: release the reservation
      // rather than leave a phantom booking in the seeker's list holding the
      // slot (see releaseUnpaidBooking).
      if (err?.code === 'no_gateway_key') {
        await releaseUnpaidBooking(bookingId, 'Payment gateway unavailable')
        showToast('Payment gateway isn’t configured yet — nothing was booked or charged. Please contact our team.')
      } else if (err?.code === 'cancelled') {
        await releaseUnpaidBooking(bookingId, 'Payment cancelled by customer')
        showToast('Payment cancelled — nothing was booked, and the slot has been released.')
      } else {
        await releaseUnpaidBooking(bookingId, 'Payment failed')
        showToast(err?.error?.message || err?.message || 'Payment could not be completed, so nothing was booked. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageLayout className="bg-[#403c3d]/5 text-[#151b21] antialiased min-h-screen flex flex-col">
      <main className="grow">
        <div className="flex flex-col min-h-screen bg-background pb-20">
          {/* ── Hero ── */}
          <div className="relative overflow-hidden bg-primary text-white pt-28 pb-14">
            <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-[#3a0a14] via-primary to-[#2a0710]" />
            <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(55%_70%_at_50%_0%,rgba(232,119,15,0.20),transparent_70%)]" />
            <div className="container px-6 mx-auto relative z-10">
              <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mb-7">
                <Link to="/" className="hover:text-secondary transition-colors">Home</Link>
                <span className="opacity-40">/</span>
                <Link to="/maharaj-ji" className="text-white/60 hover:text-secondary transition-colors">Astro Raju Maharaj Ji</Link>
                <span className="opacity-40">/</span>
                <span className="text-secondary">Gemstone Consultation</span>
              </div>
              <div className="max-w-xl">
                <div className="hero-chip inline-flex mb-5">
                  <GemIcon className="w-3.5 h-3.5 text-secondary" /> Gemstone Consultation
                </div>
                <h1 className="text-4xl md:text-5xl font-cinzel leading-tight">
                  Find Your <span className="text-gradient-gold">Gemstone</span> with the Maharaj
                </h1>
                <p className="text-white/50 text-sm mt-3">
                  {offer?.is_enabled
                    ? `Any day, ${formatTimeLabel(offer.start_time)}–${formatTimeLabel(offer.end_time)}. One fixed fee of ${priceLabel} for a ${offer.duration}-minute session.`
                    : 'Pick a day and a time that suits you.'}
                </p>
              </div>
            </div>
          </div>

          <section className="container px-6 mx-auto mt-10 relative">
            {offerPending ? (
              <div className="info-card p-8 animate-pulse h-40" aria-busy="true" />
            ) : !offer?.is_enabled ? (
              <div className="info-card p-8 text-center space-y-4">
                <h2 className="text-xl font-cinzel text-[#1C140A]">Gemstone consultations aren’t open right now</h2>
                <p className="text-sm text-[#8B7355]">Please check back soon, or book a regular audience with the Maharaj.</p>
                <Link to="/book-maharaj-ji" className="hero-btn-primary inline-flex items-center justify-center rounded-full px-7 py-3 text-[10px] font-bold uppercase tracking-[0.2em]">
                  Book an audience
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-7 space-y-8">
                  {/* ── Date strip ── */}
                  <div className="info-card overflow-hidden">
                    <div className="h-0.75 w-full bg-linear-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
                    <div className="p-6 border-b border-[#D7C29A]/20 bg-linear-to-r from-[#FFFCF5] to-[#FEF6E4]">
                      <span className="text-[10px] font-mono font-normal text-secondary uppercase tracking-[0.3em]">Every day</span>
                      <h4 className="text-base font-cinzel text-[#1C140A]">Choose a date</h4>
                    </div>
                    <div className="p-6 flex gap-2.5 overflow-x-auto">
                      {days.map((d) => {
                        const active = toIsoDate(d) === toIsoDate(selectedDate)
                        return (
                          <button
                            key={toIsoDate(d)}
                            type="button"
                            onClick={() => { setSelectedDate(d); setSelectedSlot(null) }}
                            className={`shrink-0 w-16 rounded-xl border py-3 text-center transition-all ${
                              active
                                ? 'text-white border-transparent shadow-[0_8px_18px_-6px_rgba(28,20,10,.55)]'
                                : 'border-[#D7C29A]/40 text-[#1C140A]/75 hover:border-secondary hover:text-[#1C140A]'
                            }`}
                            style={active ? { background: 'linear-gradient(135deg, #1C140A 0%, #2E1206 100%)' } : undefined}
                          >
                            <span className="block text-[9px] font-bold uppercase tracking-widest opacity-70">{WEEKDAY[d.getDay()]}</span>
                            <span className="block text-lg font-semibold leading-tight">{d.getDate()}</span>
                            <span className="block text-[9px] uppercase tracking-widest opacity-60">
                              {d.toLocaleDateString('en-IN', { month: 'short' })}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <TimeSlotPicker source="gemstone" selectedDate={selectedDate} selectedSlot={selectedSlot} onSelectSlot={setSelectedSlot} />

                  {/* ── Consultation type (only when there's a choice) ── */}
                  {offer.consultation_types.length > 1 && (
                    <div className="info-card p-6 space-y-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355]">How would you like to talk?</p>
                      <div className="flex flex-wrap gap-2">
                        {offer.consultation_types.map((t) => (
                          <button
                            key={t}
                            type="button"
                            aria-pressed={consultationType === t}
                            onClick={() => setConsultationType(t)}
                            className={`px-5 py-2.5 rounded-full text-xs font-semibold border transition-colors ${
                              consultationType === t
                                ? 'bg-[#C8922A] border-[#C8922A] text-white'
                                : 'border-[#C8922A]/25 text-[#1C140A] hover:border-[#C8922A] hover:text-[#C8922A]'
                            }`}
                          >
                            {CONSULTATION_TYPE_LABELS[t] ?? t}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <BirthDetailsForm value={birthDetails} onChange={setBirthDetails} />
                </div>

                {/* ── Summary ── */}
                <aside className="lg:col-span-5 lg:sticky lg:top-28">
                  <div className="info-card relative overflow-hidden">
                    <div className="h-0.75 w-full bg-linear-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
                    <div className="p-7 sm:p-8 space-y-6">
                      <div className="flex items-center gap-4 pb-6 border-b border-[#D7C29A]/20">
                        <img
                          src={offer.astrologer?.image_url || maharajPortrait}
                          alt="Raju Maharaj Ji"
                          className="w-14 h-14 rounded-2xl object-cover shrink-0 border-2 border-secondary/30"
                        />
                        <div className="min-w-0">
                          <p className="text-[9px] text-secondary uppercase tracking-widest mb-1">Gemstone Consultation</p>
                          <p className="text-2xl font-cinzel text-[#1C140A] leading-tight truncate">Raju Maharaj Ji</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex justify-between items-end">
                          <span className="text-primary/40 text-xs pb-1">Fee ({offer.duration} min):</span>
                          <span className="font-cinzel font-bold text-4xl text-gradient-gold">{priceLabel}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg border border-border/40">
                          <span className="text-primary/40 text-[10px] uppercase">Date</span>
                          <span className="text-primary text-xs">{formatDateLabel(selectedDate)}</span>
                        </div>
                        {selectedSlot && (
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg border border-border/40">
                            <span className="text-primary/40 text-[10px] uppercase">Time</span>
                            <span className="text-primary text-xs">{formatTimeLabel(selectedSlot.start_time)}</span>
                          </div>
                        )}
                        {consultationType && (
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg border border-border/40">
                            <span className="text-primary/40 text-[10px] uppercase">Mode</span>
                            <span className="text-primary text-xs">{CONSULTATION_TYPE_LABELS[consultationType] ?? consultationType}</span>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        disabled={!canProceed}
                        onClick={handleProceed}
                        className="hero-btn-primary premium-shine w-full inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.25em] disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Processing…' : 'Proceed to payment'}
                      </button>
                      {!birthValid && <p className="text-center text-[10px] text-[#8B7355]">Fill in your birth details to continue.</p>}
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </section>

          <BookingTrustSection />
        </div>
      </main>
      <FloatingFabs />
    </PageLayout>
  )
}
